import type { RecipeIndexEntry } from '../../../services/unifiedRecipeService';
import type { DietaryRole } from '../../../redux/mealPlanSlice';
import { classifyDietKind, type DietKind } from '../../../utils/dietClassifier';

/** The 3-way diet classification used by the generator and the config UI. */
export type { DietKind };

export const DIET_KIND_LABEL: Record<DietKind, string> = {
  fish: 'Poisson',
  veg: 'Végé',
  meat: 'Viande',
};

/** A recipe shape the classifier can read: diet tags plus title/ingredients for the fallback. */
type ClassifiableEntry = Pick<RecipeIndexEntry, 'diets' | 'title' | 'ingredients'>;

/**
 * Classify a recipe into fish / veg / meat. The `diets` tags win; when they are
 * empty or inconclusive (the norm for scraped recipes) we fall back to the FR/EN
 * title/ingredient heuristic in `classifyDietKind`. Anything unclassified is meat.
 */
export function dietKindOf(recipe: ClassifiableEntry): DietKind {
  return classifyDietKind({
    title: recipe.title ?? '',
    ingredients: recipe.ingredients ?? [],
    diets: recipe.diets,
  });
}

/** The badge role shown on cards (no role for meat — it is the default). */
export function dietaryRoleOf(recipe: ClassifiableEntry): DietaryRole | undefined {
  const kind = dietKindOf(recipe);
  if (kind === 'veg') return 'vegetarian';
  if (kind === 'fish') return 'fish';
  return undefined;
}
